import React, { useMemo, useState } from "react";
import {
  ChevronRight,
  CloudSun,
  Coffee,
  Crown,
  HandHeart,
  Heart,
  HeartHandshake,
  MapPinned,
  Rocket,
  Server,
  ShoppingBag,
  Users,
  Wrench,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import "../styles/pages/SupportApp.css";
import DesktopRightRail from "../components/DesktopRightRail";

export default function SupportApp({
  currentUser,
  posts = [],
  users = [],
  onOpenUser,
  enableDesktopRightRail = false,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openSection, setOpenSection] = useState(null);

  const toggle = (id) => {
    setOpenSection(openSection === id ? null : id);
  };

  const closeSupportApp = () => {
    navigate("/", { replace: true });
  };

  const impactItems = useMemo(
    () => [
      {
        key: "servers",
        labelKey: "support_impact_servers",
        icon: Server,
      },
      {
        key: "maps",
        labelKey: "support_impact_maps",
        icon: MapPinned,
      },
      {
        key: "weather",
        labelKey: "support_impact_weather",
        icon: CloudSun,
      },
      {
        key: "tools",
        labelKey: "support_impact_tools",
        icon: Wrench,
      },
      {
        key: "community",
        labelKey: "support_impact_community",
        icon: Users,
      },
    ],
    []
  );

  const supportOptions = useMemo(
    () => [
      {
        id: "membership",
        titleKey: "support_membership_title",
        descriptionKey: "support_membership_description",
        actionKey: "support_membership_action",
        pillKey: "support_membership_pill",
        icon: Crown,
        details: "membership",
      },
      {
        id: "coffee",
        titleKey: "support_coffee_title",
        descriptionKey: "support_coffee_description",
        actionKey: "support_coffee_action",
        pillKey: "support_coffee_pill",
        icon: Coffee,
      },
      {
        id: "custom",
        titleKey: "support_custom_title",
        descriptionKey: "support_custom_description",
        actionKey: "support_custom_action",
        pillKey: "support_custom_pill",
        icon: Heart,
      },
      {
        id: "community",
        titleKey: "support_community_title",
        descriptionKey: "support_community_description",
        actionKey: "support_community_action",
        pillKey: "support_community_pill",
        icon: Rocket,
      },
      {
        id: "merch",
        titleKey: "support_community_merch_title",
        descriptionKey: "support_community_merch_desc",
        actionKey: "support_community_merch_action",
        pillKey: "support_community_merch_pill",
        icon: ShoppingBag,
      },
      {
        id: "other",
        titleKey: "support_community_other_title",
        descriptionKey: "support_community_other_desc",
        actionKey: "support_community_other_action",
        pillKey: "support_community_other_pill",
        icon: HeartHandshake,
      },
    ],
    []
  );

  return (
    <div
      className={`support-app-layout${
        enableDesktopRightRail ? " support-app-layout--with-right-rail" : ""
      }`}
    >
      <div className="support-app-shell">
        <div className="support-container">
        <button
          type="button"
          className="support-app-close"
          onClick={closeSupportApp}
          aria-label={t("close")}
        >
          <X size={18} strokeWidth={2.4} aria-hidden="true" />
        </button>

        <section className="support-hero">
          <div className="support-hero-copy">
            <h2>{t("support_app_title")}</h2>
            <p>{t("support_app_intro_short")}</p>
          </div>
          <span className="support-hero-icon" aria-hidden="true">
            <HandHeart size={42} strokeWidth={1.9} />
          </span>
        </section>

        <section className="support-impact-card">
          <div className="support-impact-card__head">
            <h3>{t("support_impact_title")}</h3>
            <p>{t("support_impact_intro")}</p>
          </div>
          <div className="support-impact-list">
            {impactItems.map((item) => {
              const Icon = item.icon;

              return (
                <span className="support-impact-item" key={item.key}>
                  <span className="support-impact-icon" aria-hidden="true">
                    <Icon size={18} strokeWidth={2.15} />
                  </span>
                  <span>{t(item.labelKey)}</span>
                </span>
              );
            })}
          </div>
          <p className="support-impact-thanks">{t("support_impact_thanks")}</p>
        </section>

        <div className="support-options">
          {supportOptions.map((option) => {
            const Icon = option.icon;
            const isOpen = openSection === option.id;

            return (
              <section
                className={`support-card${isOpen ? " is-open" : ""}`}
                key={option.id}
              >
                <button
                  type="button"
                  className="support-card-toggle"
                  onClick={() => toggle(option.id)}
                  aria-expanded={isOpen}
                >
                  <span className="support-card-icon" aria-hidden="true">
                    <Icon size={22} strokeWidth={2.1} />
                  </span>
                  <span className="support-card-copy">
                    <span className="support-card-title">
                      {t(option.titleKey)}
                    </span>
                    <span className="support-card-description">
                      {t(option.descriptionKey)}
                    </span>
                  </span>
                  <span className="support-card-pill">{t(option.pillKey)}</span>
                  <ChevronRight
                    className="support-card-chevron"
                    size={19}
                    strokeWidth={2.4}
                    aria-hidden="true"
                  />
                </button>

                {isOpen && (
                  <div className="support-details">
                    <p>{t(option.descriptionKey)}</p>

                    {option.details === "membership" && (
                      <>
                        <ul>
                          <li>
                            <b>{t("support_membership_bronze_title")}</b>
                            <span>{t("support_membership_bronze_desc")}</span>
                          </li>
                          <li>
                            <b>{t("support_membership_silver_title")}</b>
                            <span>{t("support_membership_silver_desc")}</span>
                          </li>
                          <li>
                            <b>{t("support_membership_gold_title")}</b>
                            <span>{t("support_membership_gold_desc")}</span>
                          </li>
                        </ul>

                        <p className="support-note">
                          {t("support_membership_note")}
                        </p>
                      </>
                    )}

                    <button type="button" className="support-action">
                      {t(option.actionKey)}
                    </button>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <div className="support-app-actions">
          <button
            type="button"
            className="support-app-close-bottom"
            onClick={closeSupportApp}
          >
            {t("close")}
          </button>
        </div>
        </div>
      </div>
      {enableDesktopRightRail ? (
        <DesktopRightRail
          currentUser={currentUser}
          posts={posts}
          users={users}
          onOpenUser={onOpenUser}
        />
      ) : null}
    </div>
  );
}
